import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ShoppingCart, ArrowRight, Trash2, PhoneCall, ShieldCheck, CheckCircle2, XCircle,
  Edit3, Send, ClipboardCheck,
} from "lucide-react";
import {
  listBuyerInterests, updateBuyerInterest, deleteBuyerInterest, convertBuyerInterest,
  listBuyersByStatus, updateBuyerDraft, submitBuyerRegistration, verifyBuyer, approveBuyer, rejectBuyer,
} from "@/lib/api";

const IS_TONE = {
  new: "bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)]",
  contacted: "bg-[var(--tr-zest)]/20 text-[var(--tr-zest)]",
  converted: "bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)]",
  rejected: "bg-red-100 text-red-700",
};
const RS_TONE = {
  draft: "bg-gray-100 text-gray-700",
  submitted: "bg-[var(--tr-citrus-yellow)]/25 text-[var(--tr-deep-green)]",
  verified: "bg-[var(--tr-zest)]/20 text-[var(--tr-zest)]",
  approved: "bg-[var(--tr-lime)]/30 text-[var(--tr-deep-green)]",
  rejected: "bg-red-100 text-red-700",
};

export default function AdminBuyerWorkflow() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("interests");
  return (
    <div data-testid="admin-buyer-workflow">
      <div className="flex items-center gap-3 mb-2">
        <ShoppingCart className="w-6 h-6 text-[var(--tr-deep-green)]" />
        <h2 className="tr-section-title text-3xl">{t("admin.nav_buyers")}</h2>
      </div>
      <div className="mt-4 flex gap-2 border-b border-[rgba(6,78,59,0.15)]">
        {[
          { k: "interests", label: t("admin_p3.buyer_interests_title") },
          { k: "registrations", label: t("admin_p3.buyer_registrations_title") },
        ].map((x) => (
          <button key={x.k} data-testid={`buyer-tab-${x.k}`} onClick={() => setTab(x.k)}
            className={`px-4 py-2 -mb-px text-sm font-bold uppercase tracking-[0.14em] border-b-2 transition-colors ${tab === x.k ? "border-[var(--tr-deep-green)] text-[var(--tr-deep-green)]" : "border-transparent text-[var(--tr-muted)] hover:text-[var(--tr-deep-green)]"}`}>
            {x.label}
          </button>
        ))}
      </div>
      {tab === "interests" ? <BuyerInterestsTab /> : <BuyerRegistrationsTab />}
    </div>
  );
}

function BuyerInterestsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const load = () => listBuyerInterests(status ? { status } : {}).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const convert = async (id) => { if (!window.confirm(t("admin_p3.confirm_convert"))) return; try { const r = await convertBuyerInterest(id); toast.success(r.profile.company_name); load(); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };
  const contacted = async (id) => { try { await updateBuyerInterest(id, { status: "contacted" }); load(); } catch { toast.error(t("toast.failed")); } };
  const reject = async (id) => { if (!window.confirm(t("admin_p2.reject_confirm"))) return; try { await updateBuyerInterest(id, { status: "rejected" }); load(); } catch { toast.error(t("toast.failed")); } };
  const remove = async (id) => { if (!window.confirm(t("admin.confirm_delete"))) return; await deleteBuyerInterest(id); load(); };

  return (
    <div className="mt-6" data-testid="buyer-interests-panel">
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("admin_p3.buyer_interests_body")}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {["", "new", "contacted", "converted", "rejected"].map((s) => (
          <button key={s || "all"} data-testid={`buyer-int-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${status === s ? "bg-[var(--tr-deep-green)] text-white" : "bg-white border border-[rgba(6,78,59,0.15)] text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"}`}>
            {s || t("admin_p2.filter_all")}
          </button>
        ))}
      </div>
      {!items ? <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
        : items.length === 0 ? <div data-testid="buyer-int-empty" className="tr-card p-10 mt-6 text-center text-[var(--tr-muted)]">{t("admin_p3.empty")}</div>
        : (
          <div className="tr-card overflow-hidden mt-6" data-testid="buyer-int-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tr-sage)]/50"><tr>
                  {["Company", "Contact", "Email", "Type", "City", t("admin_p2.col_status"), t("admin_p2.col_created")].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>))}
                  <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                </tr></thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} data-testid={`buyer-int-row-${it.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                      <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{it.company_name}</td>
                      <td className="px-4 py-3">{it.contact_person}<div className="text-xs text-[var(--tr-muted)] font-mono">{it.phone}</div></td>
                      <td className="px-4 py-3 text-xs">{it.email}</td>
                      <td className="px-4 py-3 text-xs uppercase">{it.buyer_type}</td>
                      <td className="px-4 py-3">{it.city}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${IS_TONE[it.status] || "bg-gray-100 text-gray-700"}`}>{it.status}</span></td>
                      <td className="px-4 py-3 text-xs text-[var(--tr-muted)]">{new Date(it.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {it.status === "new" && <button data-testid={`buyer-int-contact-${it.id}`} onClick={() => contacted(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline"><PhoneCall className="w-4 h-4" /></button>}
                        {it.status !== "converted" && <button data-testid={`buyer-int-convert-${it.id}`} onClick={() => convert(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline"><ArrowRight className="w-4 h-4" /> {t("admin_p3.action_convert")}</button>}
                        {it.status !== "rejected" && it.status !== "converted" && <button data-testid={`buyer-int-reject-${it.id}`} onClick={() => reject(it.id)} className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold mr-3 hover:underline">{t("admin_p3.action_reject")}</button>}
                        <button data-testid={`buyer-int-delete-${it.id}`} onClick={() => remove(it.id)} className="inline-flex items-center gap-1 text-[var(--tr-muted)] hover:text-red-700 text-sm font-semibold"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
}

function BuyerRegistrationsTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => listBuyersByStatus(status ? { status } : {}).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const doVerify = async (id) => { if (!window.confirm(t("admin_p3.confirm_verify"))) return; try { await verifyBuyer(id); load(); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };
  const doApprove = async (id) => { if (!window.confirm(t("admin_p3.confirm_approve"))) return; try { await approveBuyer(id); load(); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };
  const doReject = async (id) => { const r = window.prompt(t("admin_p3.prompt_reject")); if (!r || r.trim().length < 3) return; try { await rejectBuyer(id, r.trim()); load(); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };
  const doSubmit = async (id) => { if (!window.confirm(t("admin_p3.confirm_submit"))) return; try { await submitBuyerRegistration(id); toast.success(t("admin_p3.action_submit_review")); load(); } catch (err) { const d = err?.response?.data?.detail; if (d?.error === "missing_fields") toast.error(`Missing: ${d.fields.join(", ")}`); else if (d?.error === "consent_required") toast.error(t("admin_p3.consent_note")); else toast.error(t("toast.failed")); } };
  const saveEdit = async (patch) => { try { await updateBuyerDraft(editing.id, patch); setEditing(null); load(); toast.success(t("toast.saved")); } catch (err) { toast.error(err?.response?.data?.detail?.error || t("toast.failed")); } };

  return (
    <div className="mt-6" data-testid="buyer-registrations-panel">
      <p className="text-[var(--tr-muted)] leading-relaxed max-w-3xl">{t("admin_p3.buyer_registrations_body")}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {["", "draft", "submitted", "verified", "approved", "rejected"].map((s) => (
          <button key={s || "all"} data-testid={`buyer-reg-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${status === s ? "bg-[var(--tr-deep-green)] text-white" : "bg-white border border-[rgba(6,78,59,0.15)] text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"}`}>
            {s || t("admin_p2.filter_all")}
          </button>
        ))}
      </div>
      {!items ? <div className="p-8 text-[var(--tr-muted)]">{t("common.loading")}</div>
        : items.length === 0 ? <div data-testid="buyer-reg-empty" className="tr-card p-10 mt-6 text-center text-[var(--tr-muted)]">{t("admin_p3.empty")}</div>
        : (
          <div className="tr-card overflow-hidden mt-6" data-testid="buyer-reg-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tr-sage)]/50"><tr>
                  {["Company", "Contact", "Type", "City", t("admin_p2.col_status")].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{h}</th>))}
                  <th className="text-right px-4 py-3 text-[var(--tr-deep-green)] uppercase text-[10px] tracking-[0.18em]">{t("common.actions")}</th>
                </tr></thead>
                <tbody>
                  {items.map((b) => (
                    <tr key={b.id} data-testid={`buyer-reg-row-${b.id}`} className="border-t border-[rgba(6,78,59,0.08)] hover:bg-[var(--tr-bone)]">
                      <td className="px-4 py-3 font-semibold text-[var(--tr-deep-green)]">{b.company_name}</td>
                      <td className="px-4 py-3">{b.contact_person} · <span className="font-mono">{b.phone}</span></td>
                      <td className="px-4 py-3 text-xs uppercase">{b.buyer_type}</td>
                      <td className="px-4 py-3">{b.city}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${RS_TONE[b.registration_status] || "bg-gray-100 text-gray-700"}`}>{b.registration_status || "—"}</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {(b.registration_status === "draft" || b.registration_status === "rejected") && (
                          <>
                            <button data-testid={`buyer-reg-edit-${b.id}`} onClick={() => setEditing(b)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline"><Edit3 className="w-4 h-4" /></button>
                            <button data-testid={`buyer-reg-submit-${b.id}`} onClick={() => doSubmit(b.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline"><Send className="w-4 h-4" /></button>
                          </>
                        )}
                        {b.registration_status === "submitted" && <button data-testid={`buyer-reg-verify-${b.id}`} onClick={() => doVerify(b.id)} className="inline-flex items-center gap-1 text-[var(--tr-zest)] text-sm font-semibold mr-3 hover:underline"><ShieldCheck className="w-4 h-4" /></button>}
                        {b.registration_status === "verified" && <button data-testid={`buyer-reg-approve-${b.id}`} onClick={() => doApprove(b.id)} className="inline-flex items-center gap-1 text-[var(--tr-deep-green)] text-sm font-semibold mr-3 hover:underline"><CheckCircle2 className="w-4 h-4" /></button>}
                        {(b.registration_status === "submitted" || b.registration_status === "verified") && <button data-testid={`buyer-reg-reject-${b.id}`} onClick={() => doReject(b.id)} className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold hover:underline"><XCircle className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      {editing && <BuyerEditModal buyer={editing} onClose={() => setEditing(null)} onSave={saveEdit} />}
    </div>
  );
}

function BuyerEditModal({ buyer, onClose, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company_name: buyer.company_name || "",
    contact_person: buyer.contact_person || "",
    phone: buyer.phone || "",
    email: buyer.email || "",
    buyer_type: buyer.buyer_type || "wholesale",
    city: buyer.city || "",
    state: buyer.state || "",
    country: buyer.country || "India",
    address_line: buyer.address_line || "",
    gst_number: buyer.gst_number || "",
    pan_last4: buyer.pan_last4 || "",
    bank_name: buyer.bank_name || "",
    bank_account_last4: buyer.bank_account_last4 || "",
    ifsc: buyer.ifsc || "",
    monthly_volume: buyer.monthly_volume || "",
    fssai_number: buyer.fssai_number || "",
    iec_code: buyer.iec_code || "",
    notes: buyer.notes || "",
    consent_accepted: !!buyer.consent_accepted,
  });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const save = () => onSave({ ...form, email: form.email || null });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" data-testid="buyer-edit-modal">
      <div className="tr-card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{t("admin_p3.action_edit")}: {buyer.company_name}</h3>
          <button onClick={onClose} data-testid="buyer-edit-close" className="text-[var(--tr-muted)] hover:text-[var(--tr-deep-green)]"><XCircle className="w-6 h-6" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["company_name", "Company"], ["contact_person", "Contact"], ["phone", "Phone"], ["email", "Email"],
            ["city", "City"], ["state", "State"], ["country", "Country"],
            ["address_line", "Address"], ["gst_number", "GSTIN"], ["pan_last4", "PAN last 4"],
            ["fssai_number", "FSSAI"], ["iec_code", "IEC code"], ["monthly_volume", "Monthly volume"],
          ].map(([k, l]) => (
            <label key={k} className="block">
              <span className="tr-label">{l}</span>
              <input data-testid={`buyer-edit-${k}`} className="tr-input" value={form[k]} onChange={set(k)} />
            </label>
          ))}
          <label className="block">
            <span className="tr-label">Buyer type</span>
            <select data-testid="buyer-edit-buyer_type" className="tr-input" value={form.buyer_type} onChange={set("buyer_type")}>
              {["retail", "wholesale", "export", "processor", "other"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className="tr-label">Notes</span>
            <textarea data-testid="buyer-edit-notes" className="tr-input tr-textarea" value={form.notes} onChange={set("notes")} />
          </label>
        </div>
        <label className="mt-4 flex items-start gap-3 cursor-pointer">
          <input type="checkbox" data-testid="buyer-edit-consent" checked={form.consent_accepted} onChange={(e) => setForm((s) => ({ ...s, consent_accepted: e.target.checked }))} className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)]" />
          <span className="text-sm text-[var(--tr-deep-green)]">{t("admin_p3.consent_note")}</span>
        </label>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} data-testid="buyer-edit-cancel" className="tr-pill-btn tr-btn-outline">{t("common.cancel")}</button>
          <button onClick={save} data-testid="buyer-edit-save" className="tr-pill-btn tr-btn-primary"><ClipboardCheck className="w-4 h-4" /> {t("common.save")}</button>
        </div>
      </div>
    </div>
  );
}
